import { SortDirection, SortDirections } from "~/modules/filters/types";
import { isNullOrUndefined } from "../isNullOrUndefined";

// // // //
// In-memory sorting functions from ChatGPT (used on Treasuries/Stablecoins/Jurisdiction pages)

function getNestedPropertyValue<Item extends Record<string, any>>(
    item: Item,
    nestedProperty: string,
): any {
    const properties = nestedProperty.split(".");
    let value: any = item;

    for (let property of properties) {
        if (isNullOrUndefined(value)) {
            return undefined;
        }
        value = value[property];
    }

    return value;
}

export function sortItemsByProperty<Item extends Record<string, any>>(props: {
    items: Item[];
    field: string | undefined;
    direction: SortDirection;
    nullsLast?: boolean;
    caseSensitive?: boolean;
}): Item[] {
    const {
        items,
        field,
        direction,
        nullsLast = false,
        caseSensitive = true,
    } = props;
    if (
        direction === SortDirections.none ||
        direction === null ||
        direction === undefined ||
        field === undefined
    ) {
        return items;
    }

    if (field === "" || isNullOrUndefined(field)) {
        return items;
    }

    return items.sort((a, b) => {
        const propertyA = getNestedPropertyValue(a, field);
        const propertyB = getNestedPropertyValue(b, field);

        if (isNullOrUndefined(propertyA) && isNullOrUndefined(propertyB)) {
            return 0;
        }

        if (isNullOrUndefined(propertyA)) {
            if (nullsLast) {
                return 1;
            }
            return direction === SortDirections.asc ? -1 : 1;
        }
        if (isNullOrUndefined(propertyB)) {
            if (nullsLast) {
                return -1;
            }
            return direction === SortDirections.asc ? 1 : -1;
        }

        // Sort array properties by length
        if (Array.isArray(propertyA) && Array.isArray(propertyB)) {
            if (nullsLast && propertyA.length === 0) {
                return 1;
            }
            if (nullsLast && propertyB.length === 0) {
                return -1;
            }

            if (propertyA.length < propertyB.length) {
                return direction === SortDirections.asc ? -1 : 1;
            }
            if (propertyA.length > propertyB.length) {
                return direction === SortDirections.asc ? 1 : -1;
            }
        }

        // Handle case sensitivity for string comparisons
        if (
            typeof propertyA === "string" &&
            typeof propertyB === "string" &&
            !caseSensitive
        ) {
            const valueA = propertyA.toLowerCase();
            const valueB = propertyB.toLowerCase();
            if (valueA < valueB) {
                return direction === SortDirections.asc ? -1 : 1;
            }
            if (valueA > valueB) {
                return direction === SortDirections.asc ? 1 : -1;
            }
            return 0;
        }

        if (propertyA < propertyB) {
            return direction === SortDirections.asc ? -1 : 1;
        }
        if (propertyA > propertyB) {
            return direction === SortDirections.asc ? 1 : -1;
        }
        return 0;
    });
}

// // // //
